import { useEffect, useMemo } from "react";
import { useQuery } from "urql";
import type {
  NewsFeedFilterCategory,
  NewsFeedSidebarFilterItem,
  NewsFeedSidebarResult,
  NewsFeedSidebarVariables,
} from "@/data-access/news-feeds/queries/news-feed-sidebar-filters-queries";
import { GetNewsFeedStaticFilterQuery } from "@/data-access/news-feeds/queries/news-feed-sidebar-filters-queries";
import { useAnalysisParamsForQueries } from "@/store/news-feed/params/useAnalysisParamsForQueries";
import useNewsFeedFilters from "./newsFeed-filter.slice";

export type SidebarFiltersInput = {
  /** Array of filter categories to fetch. Must be provided and non-empty. */
  categories: NewsFeedFilterCategory[];
  /** Whether queries should be enabled for all categories. Defaults to true. */
  enabled?: boolean;
  /** Per-category gate; when provided, overrides {@link SidebarFiltersInput.enabled} per category. */
  shouldFetchCategory?: (category: NewsFeedFilterCategory) => boolean;
};

/**
 * Fetches sidebar filter counts per category in parallel and writes to the filter store.
 * Each category fetch is independent (allSettled semantics). See {@link useNewsFeedFilters}.
 *
 * Note: This hook intentionally violates React's Rules of Hooks by calling useQuery inside a map function.
 * This is a conscious architectural decision to prioritize the following benefits:
 * - Independent loading states (non-blocking UI updates)
 * - Independent error handling (one category can fail while others succeed)
 * - Automatic scaling as dictionary expands (no hardcoded categories)
 * - Better user experience with partial results
 *
 * The violation is contained and well-understood. Each category gets its own useQuery instance
 * which provides the independent execution needed for optimal UX.
 */
const useNewsFeedSidebarFilters = ({
  categories,
  enabled = true,
  shouldFetchCategory,
}: SidebarFiltersInput) => {
  const { setFilterLoading, updateFilterData, setFilterError } =
    useNewsFeedFilters();
  const {
    feedId,
    startDate,
    endDate,
    selectedFilters: derivedParamFilters,
    chartSelection,
  } = useAnalysisParamsForQueries();

  // ⚠️ Intentional Rules of Hooks violation: useQuery called inside map
  // This is necessary for independent category queries and non-blocking UI updates
  const entries = categories.map((category) => {
    const categoryEnabled = shouldFetchCategory
      ? shouldFetchCategory(category)
      : enabled;

    // biome-ignore lint/correctness/useHookAtTopLevel: Intentional violation for independent category queries
    const [result, reexecute] = useQuery<
      NewsFeedSidebarResult,
      NewsFeedSidebarVariables
    >({
      query: GetNewsFeedStaticFilterQuery,
      variables: {
        projectionFilters: {
          ...(feedId ? { feedId } : {}),
          ...(startDate && endDate
            ? { dateRange: { startDate, endDate } }
            : {}),
          state: "ADDED",
          ...(chartSelection && { chartSelection }),
          ...(derivedParamFilters ?? {}),
        } as unknown as NewsFeedSidebarVariables["projectionFilters"],
        categories: [category],
      },
      pause: !categoryEnabled || !feedId || !startDate || !endDate,
    });

    // biome-ignore lint/correctness/useHookAtTopLevel: Intentional violation for independent loading state per category
    useEffect(() => {
      const prev = useNewsFeedFilters
        .getState()
        .filters.get(category)?.isLoading;
      if (prev === result.fetching) return;
      setFilterLoading(category, result.fetching);
    }, [category, result.fetching]);

    // biome-ignore lint/correctness/useHookAtTopLevel: Intentional violation for independent data updates per category
    useEffect(() => {
      if (result.data?.newsFeedSidebar) {
        const items: NewsFeedSidebarFilterItem[] =
          result.data.newsFeedSidebar.filter((r) => r.category === category);
        const prev = useNewsFeedFilters.getState().filters.get(category)?.data;
        const equal =
          Array.isArray(prev) &&
          prev.length === items.length &&
          prev.every(
            (p, i) =>
              p.category === items[i]?.category &&
              p.categoryLabel === items[i]?.categoryLabel &&
              p.total === items[i]?.total,
          );
        if (!equal) {
          updateFilterData(category, items);
          setFilterError(category, null);
        }
      }
    }, [category, result.data]);

    // biome-ignore lint/correctness/useHookAtTopLevel: Intentional violation for independent error handling per category
    useEffect(() => {
      if (result.error) {
        const prev = useNewsFeedFilters.getState().filters.get(category);
        const errorMessage = result.error.message;

        // Only update if error state has changed to prevent infinite loops
        if (prev?.error !== errorMessage) {
          setFilterError(category, errorMessage);
        }

        // Ensure loading state is cleared on error
        if (prev?.isLoading) {
          setFilterLoading(category, false);
        }
      }
    }, [category, result.error]);

    return [
      category,
      {
        isLoading: result.fetching,
        error: result.error ?? null,
        refetch: () => reexecute({ requestPolicy: "network-only" }),
      },
    ] as const;
  });

  const byCategory = useMemo(() => {
    const map = new Map<
      NewsFeedFilterCategory,
      { isLoading: boolean; error: Error | null; refetch: () => void }
    >();
    for (const [cat, info] of entries) {
      map.set(cat, info);
    }
    return map;
  }, [entries]);

  return {
    byCategory,
    categories,
  } as const;
};

export default useNewsFeedSidebarFilters;
